import React, { useEffect, useState } from 'react';

import { Group, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useAthleteFiles from 'hooks/athleteFile/useAthleteFiles';
import { CellProps } from 'react-table';
import { File as CustomFile } from 'types';

import FileDeleteModal from './FileDeleteModal';

type UploadedFiles = {
  fileId: string;
  description: string;
  value: Date;
  athleteId: string;
};
const UploadedFilesTable = ({
  searchTerm,
  handleEditFile,
  athleteId,
  permission = true,
}: {
  searchTerm?: string;
  handleEditFile: (id: string) => void;
  permission?: boolean;
  athleteId?: string;
}): JSX.Element => {
  const theme = useMantineTheme();
  const { dateTimeFormat } = useLocalization();

  const [fileId, setFileId] = useState<string>('');
  const [isDeleteModalOpened, { toggle: handleDeleteModalToggle }] = useDisclosure(false);
  const [ordering, setOrdering] = useState('status');
  const [ascending, setAscending] = useState(true);
  const [tableData, setTableData] = useState<CustomFile[]>([]);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useAthleteFiles({
      ordering: ascending ? ordering : `-${ordering}`,
      search: searchTerm,
      athlete: athleteId,
    });

  const handleDelete = (file: UploadedFiles): void => {
    setFileId(file.fileId);
    handleDeleteModalToggle();
  };

  const handleHeaderClick = (header: string): void => {
    setOrdering(header);
    setAscending(a => !a);
  };

  const columns = [
    {
      Header: 'Date & Time',
      accessor: 'createdAt',
      Cell: ({ value }: CellProps<UploadedFiles>): JSX.Element => {
        return <>{dayjs(value).format(dateTimeFormat)}</>;
      },
    },
    {
      Header: 'Description',
      accessor: 'content',
      Cell: ({ row }: CellProps<UploadedFiles>) => (
        <Text truncate='end' maw={300} onClick={() => handleEditFile(row.original.fileId)}>
          {row.original.description}
        </Text>
      ),
    },
    {
      id: 'actions',
      disableSortBy: true,
      Cell: ({ row }: CellProps<UploadedFiles>) => {
        return (
          <Group position='right' px='xs'>
            <Menu
              disabled={!permission}
              items={[
                {
                  title: 'Edit',
                  icon: <IconEdit size={theme.fontSizes.sm} />,
                  onClick: () => handleEditFile(row.original.fileId),
                },
                {
                  title: 'Delete',
                  icon: <IconTrashX size={theme.fontSizes.sm} />,
                  color: 'red',
                  onClick: () => handleDelete(row.original),
                },
              ]}
            />
          </Group>
        );
      },
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        columns={columns}
        tableData={tableData}
        isSuccess={isSuccess}
        isError={isError}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['createdAt']}
        handleHeaderClick={handleHeaderClick}
        ascending={ascending}
        ordering='createdAt'
      />
      <FileDeleteModal opened={isDeleteModalOpened} fileId={fileId} toggleDrawer={handleDeleteModalToggle} />
    </>
  );
};

export default UploadedFilesTable;
